import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useParams } from "react-router-dom";
import { firestore } from "../../config/FirebaseConfig";
import toCamelCase from "../../logic/toCamelCase";
import FetchEvents from "../../api/FetchEvents";
import FetchMapImage from "../../api/FetchMapImage";
import FetchLogoImage from "../../api/FetchLogoImage";
import FetchParticipant from "../../api/FetchParticipant";
import { FetchSponsors } from "../../api/FetchSponsors";
import "../../styles/views/public-gathering-view.css";

const normalizeArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return Object.values(value);
  return [];
};

const safeText = (value, fallback = "") => {
  if (value === undefined || value === null) return fallback;
  return String(value);
};

const formatDateLabel = (dateValue) => {
  if (!dateValue) return "TBA";
  const parsed = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return safeText(dateValue, "TBA");

  return parsed.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return "Dates coming soon";

  const start = formatDateLabel(startDate);
  const end = formatDateLabel(endDate);

  if (!endDate || startDate === endDate) return start;
  return `${start} - ${end}`;
};

const formatTimeLabel = (timeValue) => {
  if (!timeValue) return "";
  const raw = String(timeValue).trim();

  if (/am|pm/i.test(raw)) return raw.toUpperCase();

  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return raw;

  let hours = Number(match[1]);
  const minutes = match[2];
  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${suffix}`;
};

const eventSortKey = (event) => {
  const datePart = safeText(event.startDate, "9999-12-31");
  const timePart = safeText(event.startTime, "23:59");
  return `${datePart} ${timePart}`;
};

const initialsFromName = (name) => {
  const parts = safeText(name)
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return "IT";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
};

const fetchGatheringByShortcode = async (shortcode) => {
  const candidates = Array.from(
    new Set([shortcode, shortcode.toUpperCase(), shortcode.toLowerCase()]),
  );

  for (const candidate of candidates) {
    const gatheringQuery = query(
      collection(firestore, "Gatherings"),
      where("Shortcode", "==", candidate),
      where("Published", "==", true),
    );
    const gatheringSnapshot = await getDocs(gatheringQuery);
    if (!gatheringSnapshot.empty) {
      return gatheringSnapshot.docs[0];
    }
  }

  return null;
};

const fetchVendorProfile = async (vendorReference) => {
  const byReference = await FetchParticipant(String(vendorReference), "Vendor", true);
  if (byReference) return byReference;
  return FetchParticipant(String(vendorReference), "Vendor", false);
};

export default function PublicGatheringView() {
  const { shortcode } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [gathering, setGathering] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [locationFilter, setLocationFilter] = useState("All locations");
  const [dateFilter, setDateFilter] = useState("All dates");
  const [collapsedDates, setCollapsedDates] = useState({});

  useEffect(() => {
    let isMounted = true;

    const loadPublicGathering = async () => {
      setLoading(true);
      setError("");

      try {
        const normalizedShortcode = safeText(shortcode).trim();
        if (!normalizedShortcode) {
          throw new Error("Missing shortcode.");
        }

        const gatheringDoc = await fetchGatheringByShortcode(normalizedShortcode);
        if (!gatheringDoc) {
          throw new Error("Event not found.");
        }
        const gatheringData = toCamelCase({
          id: gatheringDoc.id,
          ...gatheringDoc.data(),
        });

        if (!gatheringData.published || !gatheringData.visible) {
          throw new Error("This event is not currently public.");
        }

        const [eventsRaw, sponsorsRaw, logoImage, mapImage] = await Promise.all([
          FetchEvents(gatheringData.id),
          FetchSponsors(gatheringData.id),
          FetchLogoImage(gatheringData.id),
          FetchMapImage(gatheringData.id),
        ]);

        const vendorIDs = normalizeArray(gatheringData.vendorIDs);
        const vendorProfiles = await Promise.all(
          vendorIDs.map((vendorRefID) => fetchVendorProfile(vendorRefID)),
        );

        const cleanVendors = vendorProfiles
          .filter(Boolean)
          .map((vendor) => toCamelCase(vendor))
          .sort((a, b) => safeText(a.name).localeCompare(safeText(b.name)));

        const cleanSponsors = Array.isArray(sponsorsRaw)
          ? sponsorsRaw
              .map((sponsor) => toCamelCase(sponsor))
              .sort((a, b) => (a.rank || 0) - (b.rank || 0))
          : [];

        const cleanEvents = normalizeArray(eventsRaw)
          .map((event) => toCamelCase(event))
          .map((event) => ({
            ...event,
            title: safeText(event.eventName, "Untitled event"),
            dateLabel: formatDateLabel(event.startDate),
            timeLabel: [formatTimeLabel(event.startTime), formatTimeLabel(event.endTime)]
              .filter(Boolean)
              .join(" - "),
            locationLabel: safeText(event.location, "Location TBA"),
          }))
          .sort((a, b) => eventSortKey(a).localeCompare(eventSortKey(b)));

        const withImages = {
          ...gatheringData,
          logoImage,
          mapImage,
        };

        if (isMounted) {
          setGathering(withImages);
          setSchedule(cleanEvents);
          setVendors(cleanVendors);
          setSponsors(cleanSponsors);
        }
      } catch (loadError) {
        console.error("Error loading public gathering:", loadError);
        if (isMounted) {
          setError(loadError?.message || "Unable to load this event.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPublicGathering();

    return () => {
      isMounted = false;
    };
  }, [shortcode]);

  const locationOptions = useMemo(() => {
    const allLocations = Array.from(
      new Set(
        schedule
          .map((event) => event.locationLabel)
          .filter((value) => safeText(value).trim().length > 0),
      ),
    );

    return ["All locations", ...allLocations];
  }, [schedule]);

  const dateOptions = useMemo(() => {
    const allDates = Array.from(
      new Set(
        schedule
          .map((event) => event.startDate)
          .filter((value) => safeText(value).trim().length > 0),
      ),
    );

    return ["All dates", ...allDates];
  }, [schedule]);

  useEffect(() => {
    if (!schedule.length) return;

    const groupedKeys = Array.from(
      new Set(schedule.map((event) => safeText(event.startDate, "No Date"))),
    );

    const defaultState = {};
    groupedKeys.forEach((key, index) => {
      defaultState[key] = index !== 0;
    });

    setCollapsedDates(defaultState);
  }, [schedule]);

  const filteredSchedule = useMemo(() => {
    return schedule.filter((event) => {
      const dateMatch =
        dateFilter === "All dates" || safeText(event.startDate) === dateFilter;
      const locationMatch =
        locationFilter === "All locations" || event.locationLabel === locationFilter;

      return dateMatch && locationMatch;
    });
  }, [dateFilter, locationFilter, schedule]);

  const scheduleGroups = useMemo(() => {
    const grouped = filteredSchedule.reduce((acc, event) => {
      const dateKey = safeText(event.startDate, "No Date");
      if (!acc[dateKey]) {
        acc[dateKey] = {
          dateKey,
          dateLabel: formatDateLabel(event.startDate),
          events: [],
        };
      }
      acc[dateKey].events.push(event);
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) =>
      safeText(a.dateKey).localeCompare(safeText(b.dateKey)),
    );
  }, [filteredSchedule]);

  const toggleDate = (dateKey) => {
    setCollapsedDates((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey],
    }));
  };

  if (loading) {
    return (
      <div className="public-gathering-page">
        <div className="public-gathering-shell">
          <div className="public-gathering-card public-gathering-loading">Loading event details...</div>
        </div>
      </div>
    );
  }

  if (error || !gathering) {
    return (
      <div className="public-gathering-page">
        <div className="public-gathering-shell">
          <div className="public-gathering-card public-gathering-error">
            <h1>Event unavailable</h1>
            <p>{error || "This event cannot be displayed right now."}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-gathering-page">
      <div className="public-gathering-shell">
        <header className="public-gathering-hero">
          <div className="public-gathering-logo-wrap">
            {gathering.logoImage ? (
              <img src={gathering.logoImage} alt={`${gathering.gatheringName} logo`} className="public-gathering-logo" />
            ) : (
              <div className="public-gathering-logo-fallback">{initialsFromName(gathering.gatheringName)}</div>
            )}
          </div>
          <div className="public-gathering-hero-text">
            <p className="public-gathering-kicker">Public Event Dashboard</p>
            <h1>{safeText(gathering.gatheringName, "Untitled event")}</h1>
            <p className="public-gathering-description">{safeText(gathering.description, "Event details coming soon.")}</p>
            <div className="public-gathering-meta">
              <span>{formatDateRange(gathering.startDate, gathering.endDate)}</span>
              <span>{safeText(gathering.location, "Location TBA")}</span>
            </div>
          </div>
        </header>

        <section className="public-gathering-card">
          <div className="public-section-header">
            <h2>Schedule</h2>
            <div className="public-filters">
              <select
                value={dateFilter}
                onChange={(event) => setDateFilter(event.target.value)}
                aria-label="Filter schedule by date"
              >
                {dateOptions.map((dateOption) => (
                  <option key={dateOption} value={dateOption}>
                    {dateOption === "All dates" ? dateOption : formatDateLabel(dateOption)}
                  </option>
                ))}
              </select>
              <select
                value={locationFilter}
                onChange={(event) => setLocationFilter(event.target.value)}
                aria-label="Filter schedule by location"
              >
                {locationOptions.map((locationOption) => (
                  <option key={locationOption} value={locationOption}>
                    {locationOption}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {scheduleGroups.length === 0 ? (
            <p className="public-empty-state">No events match your current filters.</p>
          ) : (
            <div className="public-date-groups">
              {scheduleGroups.map((group) => (
                <div key={group.dateKey} className="public-date-group">
                  <button
                    type="button"
                    className="public-date-toggle"
                    onClick={() => toggleDate(group.dateKey)}
                    aria-expanded={!collapsedDates[group.dateKey]}
                  >
                    <span>{group.dateLabel}</span>
                    <span>{collapsedDates[group.dateKey] ? "+" : "-"}</span>
                  </button>
                  {!collapsedDates[group.dateKey] && (
                    <div className="public-events-list">
                      {group.events.map((event) => (
                        <article key={event.id} className="public-event-item">
                          <div>
                            <h3>{event.title}</h3>
                            <p>{safeText(event.description, "No description provided.")}</p>
                          </div>
                          <div className="public-event-aside">
                            <span>{event.timeLabel || "Time TBA"}</span>
                            <span>{event.locationLabel}</span>
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="public-grid-two-up">
          <article className="public-gathering-card">
            <h2>Vendors</h2>
            {vendors.length === 0 ? (
              <p className="public-empty-state">No vendors are listed yet.</p>
            ) : (
              <div className="public-chip-grid">
                {vendors.map((vendor) => (
                  <a
                    key={vendor.id || vendor.referenceID}
                    href={vendor.website || "#"}
                    target={vendor.website ? "_blank" : undefined}
                    rel={vendor.website ? "noopener noreferrer" : undefined}
                    className="public-chip-card"
                  >
                    <h3>{safeText(vendor.name, "Vendor")}</h3>
                    <p>{safeText(vendor.description, "")}</p>
                  </a>
                ))}
              </div>
            )}
          </article>

          <article className="public-gathering-card">
            <h2>Sponsors</h2>
            {sponsors.length === 0 ? (
              <p className="public-empty-state">No sponsors are listed yet.</p>
            ) : (
              <div className="public-chip-grid">
                {sponsors.map((sponsor) => (
                  <a
                    key={sponsor.id}
                    href={sponsor.website || "#"}
                    target={sponsor.website ? "_blank" : undefined}
                    rel={sponsor.website ? "noopener noreferrer" : undefined}
                    className="public-chip-card"
                  >
                    <h3>{safeText(sponsor.name, "Sponsor")}</h3>
                    <p>{safeText(sponsor.description, "")}</p>
                  </a>
                ))}
              </div>
            )}
          </article>
        </section>

        <section className="public-gathering-card">
          <h2>Map</h2>
          {gathering.mapImage ? (
            <img
              src={gathering.mapImage}
              alt={`${gathering.gatheringName} map`}
              className="public-gathering-map"
            />
          ) : (
            <p className="public-empty-state">Map will be added soon.</p>
          )}
        </section>
      </div>
    </div>
  );
}
